import { cn } from "@/lib/utils";

// Badge de estado ÚNICO para toda la plataforma. Antes había tres sistemas distintos para el
// mismo concepto: pastel `bg-green-100/text-green-800` (Reservas), `bg-emerald-500/15` (Facturación)
// y `emerald-500/10` (Dashboard). Aquí queda un único estilo (tinte suave `/15`) con un set de
// tonos semánticos; cada dominio mapea su estado a un tono con los helpers de abajo.

export type Tone =
  | "neutral"
  | "green"
  | "emerald"
  | "teal"
  | "blue"
  | "sky"
  | "yellow"
  | "amber"
  | "orange"
  | "red"
  | "rose";

// Clases completas (Tailwind no admite construcción dinámica de nombres de clase).
const TONES: Record<Tone, string> = {
  neutral: "bg-muted text-muted-foreground",
  green: "bg-green-500/15 text-green-700 dark:text-green-400",
  emerald: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  teal: "bg-teal-500/15 text-teal-700 dark:text-teal-400",
  blue: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  sky: "bg-sky-500/15 text-sky-700 dark:text-sky-400",
  yellow: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400",
  amber: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  orange: "bg-orange-500/15 text-orange-700 dark:text-orange-400",
  red: "bg-red-500/15 text-red-700 dark:text-red-400",
  rose: "bg-rose-500/15 text-rose-700 dark:text-rose-400",
};

export function Badge({
  tone = "neutral",
  className,
  children,
}: {
  tone?: Tone;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

// Estado de reserva -> tono. Único sitio donde vive este mapa (antes duplicado en Reservas y Recorrido).
export function tonoReserva(estado: string): Tone {
  const m: Record<string, Tone> = {
    programada: "green",
    intervalo_no_activada: "yellow",
    reservada_no_usada: "orange",
    activada: "teal",
    completada: "blue",
    cancelada: "rose",
    cancelacion_tardia: "red",
  };
  return m[estado] ?? "neutral";
}

// Estado de cobro de una factura -> tono.
export function tonoFactura(estado: string): Tone {
  const m: Record<string, Tone> = {
    cobrado: "emerald",
    anulada: "neutral",
    reintento: "red",
    pendiente: "amber",
  };
  return m[estado] ?? "sky";
}
